import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTitle } from '../../context/TitleContext';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { logout, enable2FA, clear2FA, verify2FA } from '../../redux/Slices/authSlice';
import { setUserRoles } from '../../redux/Slices/PermissionsSlice';
import { logout as storeLogout } from '../../redux/Slices/storeAuthSlice';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../utils/toastHelper';
import "react-toastify/dist/ReactToastify.css";
import { IoIosArrowDown } from 'react-icons/io';
import { FaBell, FaMobileAlt, FaQrcode, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import useNotifications from '../../hooks/useNotifications';
import { getWebFcmToken } from '../../firebase/webPush';
import { isNotificationSoundEnabled, setNotificationSoundEnabled, playNotificationSound } from '../utils/notificationSound';

const Navbar = ({ toggleSideNav, isSideNavOpen }) => {
  // التأكد من أن toggleSideNav هو function صحيح
  const safeToggleSideNav = useCallback(() => {
    try {
      if (typeof toggleSideNav === 'function') {
        toggleSideNav();
      }
    } catch (error) {
    }
  }, [toggleSideNav]);
  const { t, i18n } = useTranslation("global");
  const { title } = useTitle();
  const [isMobileScrolled, setIsMobileScrolled] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // استخدام Redux store للحصول على بيانات المستخدم
  const user = useSelector((state) => state.auth.user);
  const { twoFAData, twoFAEmail } = useSelector((state) => state.auth);

  // fallback للبيانات من sessionStorage في حالة عدم وجود بيانات في Redux store
  const [userData, setUserData] = useState(null);
  const [show2FAPromptModal, setShow2FAPromptModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [loading2FA, setLoading2FA] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  const getUserData = () => {
    if (user) return { ...user, role: 'admin' };
    const storedAdmin = sessionStorage.getItem("adminkhadamat");
    if (storedAdmin) {
      try {
        const parsed = JSON.parse(storedAdmin);
        if (parsed && parsed !== "null" && parsed.data && (parsed.data.token?.access_token || parsed.data.access_token)) {
          return { ...parsed, role: 'admin' };
        }
      } catch { }
    }
    const storedStore = sessionStorage.getItem("storekhadamat");
    if (storedStore) {
      try {
        const parsed = JSON.parse(storedStore);
        if (parsed && (parsed.role === 'store' || parsed.role === 'pharmacy' || parsed.role === 'pharmacy_branch') && parsed.token) {
          return parsed;
        }
      } catch { }
    }
    const storedBranch = sessionStorage.getItem("branchkhadamat");
    if (storedBranch) {
      try {
        const parsed = JSON.parse(storedBranch);
        if (parsed && parsed.role === 'branch') {
          return parsed;
        }
      } catch { }
    }
    const storedDoctor = sessionStorage.getItem("doctorKhadamat");
    if (storedDoctor) {
      try {
        const parsed = JSON.parse(storedDoctor);
        if (parsed && parsed.role === 'doctor' && parsed.token) {
          return parsed;
        }
      } catch { }
    }
    const storedClinic = sessionStorage.getItem("clinicKhadamat");
    if (storedClinic) {
      try {
        const parsed = JSON.parse(storedClinic);
        if (parsed && parsed.role === 'clinic' && parsed.token) {
          return parsed;
        }
      } catch { }
    }
    return null;
  };

  useEffect(() => {
    setUserData(getUserData());
  }, [refreshKey, user]);

  useEffect(() => {
    const handleStorageChange = () => {
      setUserData(getUserData());
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const currentUserData = getUserData();
      if (!currentUserData && userData) {
        setUserData(null);
        setRefreshKey(prev => prev + 1);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [userData]);

  const { notifications, unreadCount, loading } = useNotifications();

  // تفضيل صوت الإشعارات (تشغيل/كتم)
  const [soundEnabled, setSoundEnabled] = useState(isNotificationSoundEnabled());

  const toggleNotificationSound = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const next = !soundEnabled;
    setSoundEnabled(next);
    setNotificationSoundEnabled(next);
    // تشغيل نغمة تجريبية عند التفعيل
    if (next) {
      playNotificationSound({ force: true });
    }
  };

  const today = new Date();
  const dayNames = [
    t("labels.week.sunday"),
    t("labels.week.monday"),
    t("labels.week.tuesday"),
    t("labels.week.wednesday"),
    t("labels.week.thursday"),
    t("labels.week.friday"),
    t("labels.week.saturday"),
  ];
  const day = dayNames[today.getDay()];
  const dateStr = `${String(today.getDate()).padStart(2, "0")}/${String(
    today.getMonth() + 1
  ).padStart(2, "0")}/${today.getFullYear()}`;
  const handleLogout = () => {
    setIsLoggingOut(true);
    const hasAdminData = sessionStorage.getItem("adminkhadamat");
    const hasStoreData = sessionStorage.getItem("storekhadamat");
    if (userData?.role === 'store' || userData?.role === 'pharmacy' || userData?.role === 'pharmacy_branch') {
      sessionStorage.removeItem("storekhadamat");
      dispatch(storeLogout());
    } else if (userData?.role === 'branch') {
      sessionStorage.removeItem("branchkhadamat");
      dispatch(logout());
    } else if (hasAdminData) {
      sessionStorage.removeItem("adminkhadamat");
      dispatch(logout());
    }
    setUserData(null);
    setRefreshKey(prev => prev + 1);
    if (hasAdminData) {
      window.location.href = '/admin-login';
    } else {
      window.location.href = '/login';
    }
    setTimeout(() => {
      showSuccessToast("تم تسجيل الخروج بنجاح");
    }, 100);
  };
  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
  }, [i18n.language]);

  useEffect(() => {
    const handleStorageChange = () => {
      setRefreshKey(prev => prev + 1);
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const handleScroll = () => {
      try {
        if (isMounted && typeof window !== 'undefined' && window.innerWidth !== undefined && window.scrollY !== undefined) {
          const isMobile = window.innerWidth <= 768;
          const scrolled = window.scrollY >= 100;
          setIsMobileScrolled(isMobile && scrolled);
        }
      } catch (error) { }
    };
    try {
      handleScroll();
      if (typeof window !== 'undefined') {
        window.addEventListener("scroll", handleScroll);
        window.addEventListener("resize", handleScroll);
      }
    } catch (error) { }
    return () => {
      isMounted = false;
      try {
        if (typeof window !== 'undefined') {
          window.removeEventListener("scroll", handleScroll);
          window.removeEventListener("resize", handleScroll);
        }
      } catch (error) { }
    };
  }, []);

  // Show 2FA prompt on home when login without 2FA
  useEffect(() => {
    try {
      const flag = sessionStorage.getItem('show2FAPromptHome');
      const adminData = sessionStorage.getItem('adminkhadamat');
      if (flag === '1' && adminData) {
        setShow2FAPromptModal(true);
      }
    } catch { }
  }, []);

  useEffect(() => {
    if (twoFAData?.qr_svg && twoFAData?.secret) {
      setShowQRModal(true);
    }
  }, [twoFAData]);

  const handleEnable2FAFromHome = async () => {
    try {
      setLoading2FA(true);
      await dispatch(enable2FA()).unwrap();
      setShow2FAPromptModal(false);
      sessionStorage.removeItem('show2FAPromptHome');
    } catch (e) {
    } finally {
      setLoading2FA(false);
    }
  };

  const handleContinueWithout2FAHome = () => {
    sessionStorage.removeItem('show2FAPromptHome');
    setShow2FAPromptModal(false);
  };

  const getAdminEmail = () => {
    // مصادر متعددة: Redux twoFAEmail -> cached -> adminkhadamat -> admin2FATemp -> Redux user
    if (twoFAEmail) return twoFAEmail;
    try {
      const cached = sessionStorage.getItem('adminEmail');
      if (cached) return cached;
    } catch { }
    try {
      const stored = sessionStorage.getItem('adminkhadamat');
      if (stored) {
        const parsed = JSON.parse(stored);
        const fromStored = (
          parsed?.data?.email ||
          parsed?.data?.user?.email ||
          parsed?.email ||
          ''
        );
        if (fromStored) return fromStored;
      }
    } catch { }
    try {
      const tmp = sessionStorage.getItem('admin2FATemp');
      if (tmp) {
        const parsed = JSON.parse(tmp);
        if (parsed?.email) return parsed.email;
      }
    } catch { }
    try {
      const fromUser = user?.data?.email || user?.email;
      if (fromUser) return fromUser;
    } catch { }
    return '';
  };

  return (
    <>
      <div
        key={refreshKey}
        className={`navbar topnav border-bottom shadow-sm ${isSideNavOpen ? "with-sidenav" : "full-width"
          } ${isMobileScrolled ? "fixed-top" : ""}`}
      >
        <div className="container d-flex justify-content-between align-items-center">
          <div className="toggle_sidenav d-flex align-items-center gap-2">
            <span
              className="p-3 main-color"
              onClick={safeToggleSideNav}
              style={{ cursor: "pointer" }}
              aria-label="Toggle Side Navigation"
            >
              <i className="fa fa-bars"></i>
            </span>
            <b className="text-md main-color">{title}</b>
          </div>

          <ul className="actions d-flex align-items-center top-actions-top list-unstyled m-0">
            <li className="text-sm gray-color">
              {t("labels.today")} : {day} {dateStr}
            </li>

            {(userData?.role === 'admin' || userData?.role === 'pharmacy' || userData?.role === 'pharmacy_branch' || userData?.role === 'store') && (
              <li className="dropdown mx-3">
                <button
                  className="btn position-relative"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  aria-label="Notifications"
                >
                  <FaBell className="text-dark" />
                  {unreadCount > 0 && (
                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                      {unreadCount}
                    </span>
                  )}
                </button>
                <ul className="dropdown-menu dropdown-menu-end p-0" style={{ minWidth: '320px', maxWidth: '400px' }}>
                  <li className="p-2 border-bottom d-flex justify-content-between align-items-center">
                    <span>
                      <strong>الإشعارات</strong>
                      {loading && <span className="ms-2 small text-muted">(جاري التحميل...)</span>}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm p-1 border-0 bg-transparent"
                      onClick={toggleNotificationSound}
                      title={soundEnabled ? 'كتم صوت الإشعارات' : 'تشغيل صوت الإشعارات'}
                      aria-label={soundEnabled ? 'كتم صوت الإشعارات' : 'تشغيل صوت الإشعارات'}
                    >
                      {soundEnabled ? (
                        <FaVolumeUp className="text-success" />
                      ) : (
                        <FaVolumeMute className="text-muted" />
                      )}
                    </button>
                  </li>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {notifications.length === 0 ? (
                      <li className="p-3 text-center text-muted">
                        {loading ? 'جاري تحميل الإشعارات...' : 'لا توجد إشعارات'}
                      </li>
                    ) : (
                      notifications.map((n) => (
                        <li key={n.id} className={`p-2 border-bottom ${n.isNew ? 'bg-light' : ''}`}>
                          <div className="small fw-bold mb-1">
                            {n.title_en || n.title || n.title_ar || 'New notification'}
                          </div>
                          <div className="small text-muted mb-1">
                            {n.content_en || n.message || n.content_ar || 'No details'}
                          </div>
                          {n.created_at && (
                            <div className="small text-muted" style={{ fontSize: '0.75rem' }}>
                              {new Date(n.created_at).toLocaleString('ar-EG')}
                            </div>
                          )}
                        </li>
                      ))
                    )}
                  </div>
                </ul>
              </li>
            )}

            {userData && !isLoggingOut ? (
              <li className="dropdown" key={refreshKey}>
                <button
                  className="dropdown-toggle user-btn border-0 text-sm"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  aria-label="User Menu"
                >
                  <div className="icon">
                    <img src={userData?.logo || userData?.store?.logo || "/logokhadamat.png"} alt="fav" style={{ objectFit: 'cover' }} />
                  </div>{" "}
                  <span className="mx-3 text-sm">
                    {userData?.name || userData?.store?.name || (
                      userData?.role === 'store'
                        ? (i18n.language === "ar" ? "متجر" : "Store")
                        : userData?.role === 'pharmacy'
                          ? (i18n.language === "ar" ? "صيدليات" : "Pharmacy")
                          : userData?.role === 'pharmacy_branch'
                            ? (i18n.language === "ar" ? "فرع صيدلية" : "Pharmacy Branch")
                            : userData?.role === 'branch'
                              ? (i18n.language === "ar" ? "فرع" : "Branch")
                              : userData?.role === 'doctor'
                                ? (i18n.language === "ar" ? "دكتور" : "Doctor")
                                : userData?.role === 'clinic'
                                  ? (i18n.language === "ar" ? "عيادة" : "Clinic")
                                  : (i18n.language === "ar" ? "أدمن" : "Admin")
                    )}
                  </span>{" "}
                  <IoIosArrowDown />
                </button>
                <ul className="dropdown-menu dropdown-menu-end shadow-lg border-0" style={{ borderRadius: '12px', minWidth: '220px' }}>
                  {(userData?.role === 'store' || userData?.role === 'pharmacy' || userData?.role === 'pharmacy_branch') && (
                    <li className="px-3 py-3 border-bottom mb-2 bg-light" style={{ borderRadius: '12px 12px 0 0' }}>
                      <div className="d-flex align-items-center gap-2">
                        <img
                          src={userData?.logo || userData?.store?.logo || "/logokhadamat.png"}
                          alt="logo"
                          style={{ width: '45px', height: '45px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #fff', shadow: '0 2px 4px rgba(0,0,0,0.1)' }}
                        />
                        <div style={{ overflow: 'hidden' }}>
                          <div className="fw-bold text-dark text-truncate" style={{ fontSize: '13px' }}>{userData?.name || userData?.store?.name}</div>
                          <div className="text-muted" style={{ fontSize: '11px' }}>{userData?.phone || userData?.store?.phone}</div>
                          <div className="badge bg-primary-subtle text-primary mt-1" style={{ fontSize: '9px' }}>
                            {userData?.role === 'pharmacy_branch' ? 'فرع صيدلية' : userData?.role === 'pharmacy' ? 'صيدلية' : 'متجر'}
                          </div>
                        </div>
                      </div>
                    </li>
                  )}
                  {userData?.role !== 'store' && userData?.role !== 'pharmacy' && userData?.role !== 'pharmacy_branch' && userData?.role !== 'doctor' && userData?.role !== 'clinic' && (
                    <li>
                      <Link
                        to="/profile"
                        className="dropdown-item"
                        style={{ fontSize: "11px" }}
                      >
                        {t("topnav.profile")}
                      </Link>
                    </li>
                  )}
                  {userData?.role === 'doctor' && (
                    <li>
                      <Link
                        to="/doctor/profile"
                        className="dropdown-item"
                        style={{ fontSize: "11px" }}
                      >
                        الملف الشخصي
                      </Link>
                    </li>
                  )}
                  {userData?.role === 'clinic' && (
                    <li>
                      <Link
                        to="/clinic/profile"
                        className="dropdown-item"
                        style={{ fontSize: "11px" }}
                      >
                        الملف الشخصي
                      </Link>
                    </li>
                  )}
                  <li className="text-center">
                    <Link
                      to="#"
                      className="dropdown-item d-flex justify-content-center align-items-center"
                      style={{ fontSize: "11px", color: '#dc3545', fontWeight: 'bold' }}
                      onClick={handleLogout}
                    >
                      {t("topnav.logout")}
                    </Link>
                  </li>
                </ul>
              </li>
            ) : null}
          </ul>
        </div>
      </div>

      {/* 2FA Prompt Modal on Home */}
      {show2FAPromptModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '450px' }}>
            <div className="modal-content" style={{ borderRadius: '20px', overflow: 'hidden' }}>
              <div className="modal-header" style={{ background: 'linear-gradient(135deg, var(--main-color) 0%, #764ba2 100%)', color: 'white' }}>
                <h5 className="modal-title mb-0 d-flex align-items-center"><FaMobileAlt className="mx-2" /> تفعيل المصادقة الثنائية</h5>
                <button type="button" className="btn-close btn-close-white" onClick={handleContinueWithout2FAHome}></button>
              </div>
              <div className="modal-body p-5 text-center" style={{ background: '#fff' }}>
                <div className="mb-4" style={{ width: '100px', height: '100px', margin: '0 auto 25px', background: 'linear-gradient(135deg, var(--main-color) 0%, #764ba2 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FaMobileAlt style={{ fontSize: '2.5rem', color: 'white' }} />
                </div>
                <h4 className="mb-3">هل تريد تفعيل المصادقة الثنائية؟</h4>
                <p className="text-muted mb-0">تفعيل 2FA يضيف طبقة حماية إضافية لحسابك باستخدام Google Authenticator</p>
              </div>
              <div className="modal-footer justify-content-center" style={{ background: '#fff' }}>
                <button type="button" className="btn" onClick={handleContinueWithout2FAHome} style={{ background: '#f8f9fa', color: '#495057', border: '1px solid #dee2e6', borderRadius: '10px', padding: '10px 25px' }}>متابعة بدون تفعيل</button>
                <button type="button" className="btn" onClick={handleEnable2FAFromHome} disabled={loading2FA} style={{ backgroundColor: 'var(--main-color)', color: 'white', borderRadius: '10px', padding: '10px 30px' }}>{loading2FA ? '...جارٍ' : 'تفعيل 2FA'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Modal on Home */}
      {showQRModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px' }}>
            <div className="modal-content" style={{ borderRadius: '20px', overflow: 'hidden' }}>
              <div className="modal-header" style={{ background: 'linear-gradient(135deg, var(--main-color) 0%, #764ba2 100%)', color: 'white' }}>
                <h5 className="modal-title mb-0 d-flex align-items-center"><FaQrcode className="mx-2" /> تفعيل 2FA</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => { setShowQRModal(false); dispatch(clear2FA()); }}></button>
              </div>
              <div className="modal-body text-center p-5" style={{ background: '#fff' }}>
                {twoFAData?.qr_svg ? (
                  <div id="homeQrBox" style={{
                    background: '#ffffff',
                    borderRadius: '12px',
                    padding: '12px',
                    display: 'inline-block',
                    border: '1px solid #eee'
                  }}>
                    <div style={{ width: '240px', height: '240px', margin: '0 auto' }}
                      dangerouslySetInnerHTML={{ __html: twoFAData.qr_svg }} />
                  </div>
                ) : (
                  <div className="text-muted">جاري تحميل رمز QR...</div>
                )}
                {twoFAData?.secret && (
                  <div className="mt-3"><strong>Secret:</strong> <span style={{ direction: 'ltr', display: 'inline-block' }}>{twoFAData.secret}</span></div>
                )}
              </div>
              <div className="modal-footer" style={{ background: '#fff' }}>
                <button type="button" className="btn btn-secondary" onClick={() => { setShowQRModal(false); dispatch(clear2FA()); }}>إغلاق</button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => { setShowQRModal(false); setShowVerifyModal(true); }}
                  style={{ backgroundColor: 'var(--main-color)', color: 'white', borderRadius: '10px', padding: '10px 30px' }}
                >
                  متابعة: إدخال الكود
                </button>
              </div>
            </div>
          </div>
          <style>{`
          #homeQrBox svg { width: 220px !important; height: 220px !important; display:block; }
          #homeQrBox svg rect[fill="#fefefe"] { fill: #ffffff !important; }
          #homeQrBox svg path { fill: #000000 !important; }
        `}</style>
        </div>
      )}

      {/* Verify 2FA Modal on Home */}
      {showVerifyModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }}>
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '420px' }}>
            <div className="modal-content" style={{ borderRadius: '20px', overflow: 'hidden' }}>
              <div className="modal-header" style={{ background: 'linear-gradient(135deg, var(--main-color) 0%, #764ba2 100%)', color: 'white' }}>
                <h5 className="modal-title mb-0 d-flex align-items-center">إدخال كود 2FA</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => { setShowVerifyModal(false); setVerificationCode(''); }}></button>
              </div>
              <div className="modal-body p-4" style={{ background: '#fff' }}>
                <label className="form-label">كود التحقق من Google Authenticator</label>
                <input
                  type="text"
                  className="form-control verify-2fa-input"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="أدخل الكود المكون من 6 أرقام"
                  maxLength={6}
                  autoFocus
                  style={{ fontSize: '22px', textAlign: 'center', letterSpacing: '8px', fontFamily: 'monospace', fontWeight: 'bold' }}
                />
                <small className="text-muted d-block mt-2">أدخل الكود من تطبيق Google Authenticator</small>
              </div>
              <div className="modal-footer d-flex gap-2 justify-content-center" style={{ background: '#fff' }}>
                <button type="button" className="btn btn-secondary"
                  onClick={() => { setShowVerifyModal(false); setVerificationCode(''); }}
                  style={{ borderRadius: '10px', padding: '10px 30px' }}
                >
                  إلغاء
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={verificationCode.length !== 6 || verifying}
                  onClick={async () => {
                    try {
                      setVerifying(true);
                      const email = getAdminEmail();
                      if (!email) { return; }
                      let webFcmToken = '';
                      try {
                        webFcmToken = await getWebFcmToken();
                      } catch {
                        webFcmToken = '';
                      }
                      const result = await dispatch(
                        verify2FA({
                          email,
                          code: verificationCode,
                          web_fcm_token: webFcmToken || '',
                        })
                      ).unwrap();
                      // حفظ الأدوار إن وُجدت
                      if (result?.data?.admin && Array.isArray(result.data.admin)) {
                        const roles = result.data.admin.map(a => ({ role_name_en: a.role_name_en, permissions: a.permissions || [] }));
                        dispatch(setUserRoles({ roles }));
                      } else if (result?.data?.roles) {
                        dispatch(setUserRoles({ roles: result.data.roles }));
                      }
                      sessionStorage.removeItem('show2FAPromptHome');
                      setShowVerifyModal(false);
                      setVerificationCode('');
                      window.location.href = '/';
                    } catch (err) {
                      // errors handled in thunk toasts
                    } finally {
                      setVerifying(false);
                    }
                  }}
                  style={{ backgroundColor: 'var(--main-color)', color: 'white', borderRadius: '10px', padding: '10px 30px' }}
                >
                  {verifying ? 'جارٍ التحقق...' : 'تحقق'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Local styles for placeholder size */}
      <style>{`
      .verify-2fa-input::placeholder { font-size: 14px; letter-spacing: normal; }
      .verify-2fa-input::-webkit-input-placeholder { font-size: 14px; letter-spacing: normal; }
      .verify-2fa-input::-moz-placeholder { font-size: 14px; letter-spacing: normal; }
      .verify-2fa-input:-ms-input-placeholder { font-size: 14px; letter-spacing: normal; }
      .verify-2fa-input:-moz-placeholder { font-size: 14px; letter-spacing: normal; }
    `}</style>
    </>
  );
};

export default Navbar;
